import React, { lazy, Suspense, type ComponentType } from 'react';
import { Route } from 'react-router-dom';
import {
  HEAD_TO_HEAD_COMPARISONS,
  HEAD_TO_HEAD_PATHS,
  SEGMENT_GUIDES,
  SEGMENT_GUIDE_PATHS,
  headToHeadPath,
} from './constants/head-to-head';
import { DEMO_PATH } from './constants/demo-link';
import { HTML_SITEMAP_PATH } from './constants/html-sitemap-link';
import { INTEGRATIONS_PATH } from './constants/integrations';
import { REVIEW_GENERATOR_PATH } from './constants/review-question-generator';
import { TRUST_PATH } from './constants/trust';

// Route modules intentionally have different prop requirements; they are narrowed
// again by deferredPage<Props> when rendered.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageModule = { default: ComponentType<any> };
type PageLoader = () => Promise<PageModule>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const loadedPages = new Map<string, ComponentType<any>>();

const pageLoaders = {
  landing: () => import('./pages/Landing'),
  features: () => import('./pages/Features'),
  showcase: () => import('./pages/Showcase'),
  solutions: () => import('./pages/SolutionsHub'),
  pricing: () => import('./pages/Pricing'),
  integrations: () => import('./pages/IntegrationsPage'),
  trust: () => import('./pages/TrustPage'),
  comparison: () => import('./pages/BestPerformanceManagementSoftware'),
  headToHead: () => import('./pages/HeadToHeadPage'),
  segmentGuide: () => import('./pages/SegmentGuidePage'),
  campaign: () => import('./pages/PerformanceCampaignLanding'),
  about: () => import('./pages/About'),
  contact: () => import('./pages/Contact'),
  demo: () => import('./pages/DemoPage'),
  solution: () => import('./pages/SolutionPage'),
  blog: () => import('./pages/BlogIndex'),
  blogPost: () => import('./pages/BlogPostPage'),
  blogTopic: () => import('./pages/BlogTopicPage'),
  inPractice: () => import('./pages/InPracticeIndex'),
  rolloutStory: () => import('./pages/InPracticePage'),
  glossary: () => import('./pages/GlossaryIndex'),
  glossaryTerm: () => import('./pages/GlossaryTermPage'),
  resource: () => import('./pages/ResourcePage'),
  reviewGenerator: () => import('./pages/ReviewQuestionGeneratorPage'),
  htmlSitemap: () => import('./pages/HtmlSitemapPage'),
  notFound: () => import('./pages/NotFound'),
} satisfies Record<string, PageLoader>;

type PageKey = keyof typeof pageLoaders;

const loadPage = async (key: PageKey): Promise<PageModule> => {
  const page = await pageLoaders[key]() as PageModule;
  loadedPages.set(key, page.default);
  return page;
};

const deferredPage = <Props extends object = Record<string, never>>(key: PageKey) => {
  const LazyPage = lazy(
    () => loadPage(key) as Promise<{ default: ComponentType<Props> }>,
  );

  return function DeferredPage(props: Props) {
    const LoadedPage = loadedPages.get(key);

    if (LoadedPage) {
      return <LoadedPage {...props} />;
    }

    return (
      <Suspense fallback={<div className="min-h-screen bg-white" aria-busy="true" />}>
        <LazyPage {...props} />
      </Suspense>
    );
  };
};

const Landing = deferredPage('landing');
const Features = deferredPage('features');
const Showcase = deferredPage('showcase');
const SolutionsHub = deferredPage('solutions');
const Pricing = deferredPage('pricing');
const IntegrationsPage = deferredPage('integrations');
const TrustPage = deferredPage('trust');
const BestPerformanceManagementSoftware = deferredPage('comparison');
const HeadToHeadPage = deferredPage<{ slug: string }>('headToHead');
const SegmentGuidePage = deferredPage<{ path: string }>('segmentGuide');
const PerformanceCampaignLanding = deferredPage<{
  kind: 'performance' | 'ai' | 'feedback360' | 'alternative';
}>('campaign');
const About = deferredPage('about');
const Contact = deferredPage('contact');
const DemoPage = deferredPage('demo');
const SolutionPage = deferredPage<{ category: 'solutions' | 'industries' }>('solution');
const BlogIndex = deferredPage('blog');
const BlogPostPage = deferredPage('blogPost');
const BlogTopicPage = deferredPage('blogTopic');
const InPracticeIndex = deferredPage('inPractice');
const InPracticePage = deferredPage('rolloutStory');
const GlossaryIndex = deferredPage('glossary');
const GlossaryTermPage = deferredPage('glossaryTerm');
const ResourcePage = deferredPage('resource');
const ReviewQuestionGeneratorPage = deferredPage('reviewGenerator');
const HtmlSitemapPage = deferredPage('htmlSitemap');
export const NotFound = deferredPage('notFound');

const pageKeyForPath = (pathname: string): PageKey => {
  if (pathname === '/') return 'landing';
  if (pathname === '/features') return 'features';
  if (pathname === '/showcase') return 'showcase';
  if (pathname === '/solutions') return 'solutions';
  if (pathname === '/pricing') return 'pricing';
  if (pathname === INTEGRATIONS_PATH) return 'integrations';
  if (pathname === TRUST_PATH) return 'trust';
  if (pathname === '/best-performance-management-software') return 'comparison';
  if (HEAD_TO_HEAD_PATHS.includes(pathname)) return 'headToHead';
  if (SEGMENT_GUIDE_PATHS.includes(pathname)) return 'segmentGuide';
  if (
    pathname === '/performance-management-software' ||
    pathname === '/ai-performance-management' ||
    pathname === '/360-feedback-software' ||
    pathname === '/lattice-alternative'
  ) return 'campaign';
  if (pathname === '/about') return 'about';
  if (pathname === '/contact') return 'contact';
  if (pathname === DEMO_PATH) return 'demo';
  if (pathname === '/blog') return 'blog';
  if (pathname.startsWith('/blog/topic/')) return 'blogTopic';
  if (pathname.startsWith('/blog/')) return 'blogPost';
  if (pathname === '/in-practice') return 'inPractice';
  if (pathname.startsWith('/in-practice/')) return 'rolloutStory';
  if (pathname === '/glossary') return 'glossary';
  if (pathname.startsWith('/glossary/')) return 'glossaryTerm';
  if (pathname.startsWith('/resources/')) return 'resource';
  if (pathname === REVIEW_GENERATOR_PATH) return 'reviewGenerator';
  if (pathname === HTML_SITEMAP_PATH) return 'htmlSitemap';
  if (pathname.startsWith('/solutions/') || pathname.startsWith('/industries/')) return 'solution';
  return 'notFound';
};

/** Load the current route before hydration so prerendered HTML stays intact. */
// eslint-disable-next-line react-refresh/only-export-components
export const preloadMarketingRoute = (pathname: string) => loadPage(pageKeyForPath(pathname));
// eslint-disable-next-line react-refresh/only-export-components
export const getMarketingRouteElements = () => (
  <>
    <Route path="/" element={<Landing />} />
    <Route path="/features" element={<Features />} />
    <Route path="/showcase" element={<Showcase />} />
    <Route path="/solutions" element={<SolutionsHub />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path={INTEGRATIONS_PATH} element={<IntegrationsPage />} />
    <Route path={TRUST_PATH} element={<TrustPage />} />
    <Route path="/best-performance-management-software" element={<BestPerformanceManagementSoftware />} />
    {HEAD_TO_HEAD_COMPARISONS.map((entry) => (
      <Route key={entry.slug} path={headToHeadPath(entry)} element={<HeadToHeadPage slug={entry.slug} />} />
    ))}
    {SEGMENT_GUIDES.map((guide) => (
      <Route key={guide.path} path={guide.path} element={<SegmentGuidePage path={guide.path} />} />
    ))}
    <Route path="/performance-management-software" element={<PerformanceCampaignLanding kind="performance" />} />
    <Route path="/ai-performance-management" element={<PerformanceCampaignLanding kind="ai" />} />
    <Route path="/360-feedback-software" element={<PerformanceCampaignLanding kind="feedback360" />} />
    <Route path="/lattice-alternative" element={<PerformanceCampaignLanding kind="alternative" />} />
    <Route path="/about" element={<About />} />
    <Route path="/contact" element={<Contact />} />
    <Route path={DEMO_PATH} element={<DemoPage />} />
    <Route path="/blog" element={<BlogIndex />} />
    <Route path="/blog/:slug" element={<BlogPostPage />} />
    <Route path="/blog/topic/:topic" element={<BlogTopicPage />} />
    <Route path="/in-practice" element={<InPracticeIndex />} />
    <Route path="/in-practice/:slug" element={<InPracticePage />} />
    <Route path="/glossary" element={<GlossaryIndex />} />
    <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
    <Route path="/resources/:slug" element={<ResourcePage />} />
    <Route path="/resources/:section/:slug" element={<ResourcePage />} />
    <Route path={REVIEW_GENERATOR_PATH} element={<ReviewQuestionGeneratorPage />} />
    <Route path={HTML_SITEMAP_PATH} element={<HtmlSitemapPage />} />
    <Route path="/solutions/:slug" element={<SolutionPage category="solutions" />} />
    <Route path="/industries/:slug" element={<SolutionPage category="industries" />} />
  </>
);
